import clsx from 'clsx'
import debounce from 'lodash/debounce'
import filter from 'lodash/filter'
import findIndex from 'lodash/findIndex'
import merge from 'lodash/merge'
import reverse from 'lodash/reverse'
import React, { CSSProperties, forwardRef, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'

import styles from './styles.module.scss'

const mouseDragEnd = (container?: HTMLDivElement, numberOfItemsToMove: number = 1) => {
  if (!container) {
    return
  }
  const children = Array.from(container.children) as HTMLElement[]
  const { scrollLeft } = container
  // At the end
  if (scrollLeft >= container.scrollWidth - container.clientWidth) {
    container.scrollTo({
      left: container.scrollWidth,
      behavior: 'smooth',
    })
    return
  }
  // at the beginning
  if (scrollLeft === 0) {
    container.scrollTo({
      left: 0,
      behavior: 'smooth',
    })
    return
  }
  // find nearest neighbor to current position
  const filteredChildren = children.filter((_, index) => index === 0 || index === children.length - 1 || index % numberOfItemsToMove === 0)
  const nearest = filteredChildren.reduce((prev, curr) => {
    const centerOf = (el: HTMLElement) => Math.abs(el.offsetLeft - scrollLeft)
    const res = centerOf(curr) <= centerOf(prev)
    return res ? curr : prev
  })
  if (nearest) {
    container.scrollTo({
      left: nearest.offsetLeft,
      behavior: 'smooth',
    })
  }
}
const closest = (childElements: HTMLElement[], endViewPort: number): HTMLElement => childElements.reduce((prev, curr) => (
  Math.abs(curr.offsetLeft - endViewPort) < Math.abs(prev.offsetLeft - endViewPort)
    ? curr
    : prev))
const GoToNext = (container?: HTMLDivElement) => {
  if (!container) {
    return
  }
  const childElements = reverse(Array.from(container.children)) as HTMLElement[]
  const startViewPort = container.scrollLeft
  const endViewPort = startViewPort + container.offsetWidth

  // Find last visible index in current viewport
  const nextChild = closest(childElements, endViewPort)
  container.scrollTo({
    left: nextChild?.offsetLeft,
    behavior: 'smooth',
  })
}
const GoToPrev = (container?: HTMLDivElement) => {
  if (!container) {
    return
  }
  const childElements = Array.from(container.children) as HTMLElement[]
  const startViewPort = container.scrollLeft - container.offsetWidth
  // Find first visible index 1 viewport to the left
  const nextChild = closest(childElements, startViewPort)

  container.scrollTo({
    left: nextChild?.offsetLeft,
    behavior: 'smooth',
  })
}

const getScrollData: (container: HTMLDivElement) => SliderData | null = (container: HTMLDivElement) => {
  if (!container) {
    return null
  }
  const childElements = (container.children ? Array.from(container.children) : []) as HTMLElement[]
  const startViewPort = container.scrollLeft
  const endViewPort = startViewPort + container.offsetWidth
  const firstIndex = findIndex(childElements, child => child.offsetLeft >= startViewPort)
  const numberOfItemsInViewport = filter(
    childElements,
    child => child.offsetLeft >= startViewPort && (child.offsetLeft + child.clientWidth <= endViewPort),
  ).length
  const lastIndex = findIndex(reverse(childElements), child => child.offsetLeft + child.clientWidth <= endViewPort)
  return ({
    startViewPort,
    endViewPort,
    firstIndex,
    firstRawPosition: firstIndex === 0 ? 0 : childElements[firstIndex]?.scrollLeft,
    lastRawPosition: childElements[lastIndex]?.scrollLeft,
    lastIndex: Math.abs(lastIndex - (childElements.length - 1)),
    isScrollable: container.scrollWidth > container.offsetWidth,
    rangeMax: container.scrollWidth - container.offsetWidth,
    rangeValue: firstIndex === 0 ? 0 : container.scrollLeft,
    numberOfItemsInViewport,
    rangeMin: 0,
  })
}

export interface SliderData {
  endViewPort: number
  firstIndex: number
  firstRawPosition: number
  isScrollable: boolean
  lastIndex: number
  lastRawPosition: number
  numberOfItemsInViewport: number
  rangeMax: number
  rangeMin: number
  rangeValue: number
  startViewPort: number
}

export const useSlider = () => {
  const sliderRef = useRef<HTMLSliderElement | null>(null)
  const [sliderState, setSliderState] = useState<SliderData | null>(null)
  const [resize, setResize] = useState<boolean>(false)
  const onSlide = useCallback((data: SliderData, resized?: boolean) => {
    setSliderState(data)
    setResize(!!resized)
  }, [])
  return { sliderRef, onSlide, sliderState, resize }
}

interface SliderProps {
  className?: string
  debounceDelay?: number
  numberOfItemsToMove?: number
  onSlide?: (data: SliderData, resize?: boolean) => void
  style?: CSSProperties
}

export type HTMLSliderElement = HTMLDivElement & {
  next: () => void
  prev: () => void
}
export const Slider = forwardRef<HTMLSliderElement, PropsWithChildren<SliderProps>>(({
  children,
  className,
  debounceDelay = 8,
  numberOfItemsToMove = 1,
  onSlide,
  style,
}, ref) => {
  const slider = useRef<HTMLDivElement | null>(null)
  const [dragging, setDragging] = useState(false)
  const [mouseDown, setMouseDown] = useState(false)
  const [enableSnap, setEnableSnap] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  React.useImperativeHandle(ref, () => merge(slider.current!, {
    next: () => {
      if (slider?.current) {
        setMouseDown(false)
        setDragging(false)
        GoToNext(slider?.current as HTMLDivElement)
      }
    },
    prev: () => {
      if (slider?.current) {
        setMouseDown(false)
        setDragging(false)
        GoToPrev(slider?.current as HTMLDivElement)
      }
    },
  }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onScroll = useCallback(debounce((event?: any) => {
    if (onSlide && slider?.current) {
      const isResizeEvent = event && 'type' in event && event.type === 'resize'
      if (isResizeEvent) {
        mouseDragEnd(slider?.current as HTMLDivElement, numberOfItemsToMove)
        setTimeout(() => {
          const scrollData = getScrollData(slider?.current as HTMLDivElement)
          if (scrollData) {
            onSlide(scrollData, isResizeEvent)
          }
        }, 200)
        return
      }
      const scrollData = getScrollData(slider?.current as HTMLDivElement)
      if (scrollData) {
        onSlide(scrollData)
      }
    }
  }, debounceDelay), [debounceDelay, onSlide, slider])

  useEffect(() => {
    onScroll({ type: 'resize' })
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onScroll, { passive: true })
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', onScroll)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!slider?.current) {
      return
    }
    setEnableSnap(false)
    setMouseDown(true)
    setDragging(false)
    setStartX(e.pageX - slider.current.offsetLeft)
    setScrollLeft(slider.current.scrollLeft)
  }, [slider])
  const onMouseLeave = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (mouseDown || dragging) {
      mouseDragEnd(e.currentTarget, numberOfItemsToMove)
      setDragging(false)
      setMouseDown(false)
    }
  }, [dragging, mouseDown, numberOfItemsToMove])
  const onMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    if (!mouseDown || !slider?.current) {
      return
    }
    e.stopPropagation()
    const x = e.pageX - slider.current.offsetLeft
    const scroll = x - startX
    setDragging(Math.abs(scroll) > 3)
    slider.current.scrollLeft = scrollLeft - scroll
  }, [mouseDown, scrollLeft, slider, startX])
  return (
    <div
      className={clsx(className, 'slider', styles.slider, enableSnap && styles.snap, dragging && styles.mouseDown)}
      onMouseDown={onMouseDown}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseLeave}
      onScroll={onSlide ? onScroll : undefined}
      onTouchStart={() => setEnableSnap(true)}
      ref={slider}
      style={style}
    >
      {children}
    </div>
  )
})
